import { FIELD, type PitchSkin } from "@golazo/shared";
import type { Graphics } from "pixi.js";

/** RNG determinista para patrones que necesitan aleatoriedad visual. */
function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function hashSkin(skin: PitchSkin): number {
  let h = 0;
  for (let i = 0; i < skin.id.length; i++) h = (h * 31 + skin.id.charCodeAt(i)) >>> 0;
  return h;
}

function drawWear(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  if (skin.id === "terrazzo") {
    const cx = w * 0.5;
    g.circle(cx, h * 0.5, FIELD.CENTER_CIRCLE_RADIUS * 0.55).fill({
      color: skin.accent,
      alpha: 0.035,
    });
    return;
  }

  const boxTop = (h - FIELD.BOX_HEIGHT) / 2;
  g.rect(0, boxTop, FIELD.BOX_WIDTH, FIELD.BOX_HEIGHT).fill({ color: skin.wear, alpha: 0.16 });
  g.rect(w - FIELD.BOX_WIDTH, boxTop, FIELD.BOX_WIDTH, FIELD.BOX_HEIGHT).fill({
    color: skin.wear,
    alpha: 0.16,
  });
  g.circle(w * 0.5, h * 0.5, FIELD.CENTER_CIRCLE_RADIUS + 16).fill({
    color: skin.accent,
    alpha: 0.04,
  });
}

function drawEdgeVignette(g: Graphics, w: number, h: number): void {
  const edge = 10;
  g.rect(0, 0, w, edge).fill({ color: 0x000000, alpha: 0.12 });
  g.rect(0, h - edge, w, edge).fill({ color: 0x000000, alpha: 0.16 });
  g.rect(0, 0, edge, h).fill({ color: 0x000000, alpha: 0.1 });
  g.rect(w - edge, 0, edge, h).fill({ color: 0x000000, alpha: 0.1 });
}

function drawFloodlit(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  const stripeWidth = w / skin.stripes;
  g.rect(0, 0, w, h).fill(skin.base);

  for (let i = 0; i < skin.stripes; i++) {
    const lit = i % 2 === 0;
    g.rect(i * stripeWidth, 0, stripeWidth + 1, h).fill(lit ? skin.stripeB : skin.stripeA);
    if (lit) {
      g.rect(i * stripeWidth, 0, stripeWidth * 0.42, h).fill({ color: skin.accent, alpha: 0.22 });
    }
  }

  const diagStep = 34;
  for (let d = -h; d < w + h; d += diagStep) {
    const alpha = 0.028 + ((d / diagStep) % 2 === 0 ? 0.012 : 0);
    g.moveTo(d, 0)
      .lineTo(d + h, h)
      .stroke({ width: 1.4, color: 0x000000, alpha });
    g.moveTo(d + diagStep * 0.5, 0)
      .lineTo(d + h + diagStep * 0.5, h)
      .stroke({ width: 0.8, color: skin.accent, alpha: 0.02 });
  }

  const cx = w * 0.5;
  for (let i = 5; i >= 1; i--) {
    const t = i / 5;
    g.ellipse(cx, -h * 0.06, w * (0.34 + t * 0.28), h * (0.52 + t * 0.38)).fill({
      color: i % 2 === 0 ? skin.glow : skin.accent,
      alpha: 0.018 * (1 - t * 0.55),
    });
  }
}

function drawNeonHex(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const size = 28;
  const hexH = size * Math.sqrt(3);

  for (let row = -1; row < h / hexH + 1; row++) {
    for (let col = -1; col < w / size + 1; col++) {
      const offsetX = row % 2 === 0 ? 0 : size * 1.5;
      const cx = col * size * 3 + offsetX;
      const cy = row * hexH * 0.5;
      const alt = (row + col) % 2 === 0;
      drawHex(g, cx, cy, size * 0.92, alt ? skin.stripeB : skin.stripeA, 0.55);
      drawHexStroke(g, cx, cy, size * 0.92, alt ? skin.accent : skin.glow, 0.22);
    }
  }

  g.rect(0, 0, w, h).fill({ color: skin.glow, alpha: 0.035 });
}

function drawHex(g: Graphics, cx: number, cy: number, r: number, color: number, alpha: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath().fill({ color, alpha });
}

function drawHexStroke(g: Graphics, cx: number, cy: number, r: number, color: number, alpha: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath().stroke({ width: 1, color, alpha });
}

function drawSunburst(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const rays = skin.stripes * 2;
  const maxR = Math.hypot(w, h) * 0.72;

  for (let i = 0; i < rays; i++) {
    const a0 = (i / rays) * Math.PI * 2;
    const a1 = ((i + 1) / rays) * Math.PI * 2;
    const color = i % 2 === 0 ? skin.stripeB : skin.stripeA;
    g.moveTo(cx, cy)
      .lineTo(cx + Math.cos(a0) * maxR, cy + Math.sin(a0) * maxR)
      .lineTo(cx + Math.cos(a1) * maxR, cy + Math.sin(a1) * maxR)
      .closePath()
      .fill({ color, alpha: 0.88 });
  }

  g.circle(cx, cy, FIELD.CENTER_CIRCLE_RADIUS + 8).fill({ color: skin.glow, alpha: 0.12 });
  g.circle(cx, cy, FIELD.CENTER_CIRCLE_RADIUS * 0.35).fill({ color: skin.accent, alpha: 0.25 });
}

function drawTerrazzo(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const rand = seeded(hashSkin(skin));

  for (let i = 0; i < 72; i++) {
    const x = rand() * w;
    const y = rand() * h;
    const rw = 18 + rand() * 48;
    const rh = 12 + rand() * 32;
    const rot = rand() * Math.PI;
    const color = rand() > 0.5 ? skin.stripeB : skin.stripeA;
    g.ellipse(x, y, rw, rh).fill({ color, alpha: 0.35 + rand() * 0.25 });
    void rot;
  }

  for (let i = 0; i < 18; i++) {
    const x = rand() * w;
    const y = rand() * h;
    g.circle(x, y, 4 + rand() * 10).fill({ color: skin.accent, alpha: 0.08 + rand() * 0.12 });
  }

  g.rect(0, 0, w, h).fill({ color: skin.glow, alpha: 0.04 });
}

function drawSynthScan(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const bands = skin.stripes * 3;

  for (let i = 0; i < bands; i++) {
    const y = (i / bands) * h;
    const bh = h / bands + 1;
    const alt = i % 2 === 0;
    g.rect(0, y, w, bh).fill(alt ? skin.stripeB : skin.stripeA);
    if (i % 4 === 0) {
      g.rect(0, y, w, 2).fill({ color: skin.accent, alpha: 0.35 });
    }
  }

  const cx = w * 0.5;
  g.ellipse(cx, h * 1.05, w * 0.55, h * 0.45).fill({ color: skin.glow, alpha: 0.08 });
  g.rect(0, 0, w, h * 0.35).fill({ color: skin.glow, alpha: 0.06 });
  g.rect(0, h * 0.65, w, h * 0.35).fill({ color: 0x000000, alpha: 0.22 });
}

function drawCropCircles(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const cols = skin.stripes + 2;
  const rows = Math.round(cols * (h / FIELD.WIDTH));
  const cellW = w / cols;
  const cellH = h / rows;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cx = col * cellW + cellW * 0.5;
      const cy = row * cellH + cellH * 0.5;
      const alt = (row + col) % 2 === 0;
      const r = Math.min(cellW, cellH) * (alt ? 0.38 : 0.28);
      g.circle(cx, cy, r).fill({ color: alt ? skin.stripeB : skin.stripeA, alpha: 0.75 });
      g.circle(cx, cy, r * 0.55).stroke({ width: 1.5, color: skin.accent, alpha: alt ? 0.35 : 0.18 });
      if (alt) g.circle(cx, cy, r * 0.18).fill({ color: skin.glow, alpha: 0.4 });
    }
  }
}

function drawHerringbone(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const tileW = 36;
  const tileH = 18;

  for (let y = -tileH; y < h + tileH; y += tileH) {
    for (let x = -tileW; x < w + tileW; x += tileW) {
      const flip = ((x / tileW + y / tileH) | 0) % 2 === 0;
      const color = flip ? skin.stripeB : skin.stripeA;
      g.moveTo(x, y)
        .lineTo(x + tileW * 0.5, y + tileH)
        .lineTo(x + tileW, y)
        .lineTo(x + tileW * 0.5, y - tileH)
        .closePath()
        .fill({ color, alpha: 0.82 });
      g.moveTo(x, y)
        .lineTo(x + tileW * 0.5, y + tileH)
        .lineTo(x + tileW, y)
        .lineTo(x + tileW * 0.5, y - tileH)
        .closePath()
        .stroke({ width: 0.8, color: skin.accent, alpha: 0.12 });
    }
  }
}

function drawSplitDream(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  const mid = w * 0.5;
  const stripeW = mid / skin.stripes;

  g.rect(0, 0, mid, h).fill(skin.stripeA);
  for (let i = 0; i < skin.stripes; i++) {
    const warm = i % 2 === 0 ? skin.stripeB : skin.stripeA;
    g.rect(i * stripeW, 0, stripeW + 1, h).fill({ color: warm, alpha: 0.9 });
  }

  g.rect(mid, 0, mid, h).fill(skin.stripeB);
  const diagStep = 28;
  for (let d = -h; d < mid + h; d += diagStep) {
    g.moveTo(mid + d, 0)
      .lineTo(mid + d + h, h)
      .stroke({
        width: 2,
        color: (d / diagStep) % 2 === 0 ? skin.glow : skin.accent,
        alpha: 0.14,
      });
  }

  g.moveTo(mid, 0)
    .lineTo(mid, h)
    .stroke({ width: 4, color: skin.glow, alpha: 0.45 });
  g.moveTo(mid - 3, 0)
    .lineTo(mid - 3, h)
    .stroke({ width: 2, color: skin.accent, alpha: 0.35 });
}

function drawMagmaVein(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const rand = seeded(hashSkin(skin) ^ 0xdeadbeef);

  for (let i = 0; i < 9; i++) {
    const y0 = rand() * h;
    g.moveTo(0, y0);
    let x = 0;
    let y = y0;
    while (x < w) {
      x += 24 + rand() * 40;
      y += (rand() - 0.5) * 36;
      g.lineTo(x, y);
    }
    g.stroke({ width: 2 + rand() * 4, color: skin.accent, alpha: 0.25 + rand() * 0.2 });
  }

  const stripeW = w / skin.stripes;
  for (let i = 0; i < skin.stripes; i++) {
    if (i % 2 === 1) continue;
    g.rect(i * stripeW, 0, stripeW, h).fill({ color: skin.stripeB, alpha: 0.35 });
  }

  g.rect(0, 0, w, h).fill({ color: skin.glow, alpha: 0.04 });
}

function drawCrystalShard(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);
  const rand = seeded(hashSkin(skin) ^ 0xc157a1);
  const shards = 48;

  for (let i = 0; i < shards; i++) {
    const cx = rand() * w;
    const cy = rand() * h;
    const size = 16 + rand() * 42;
    const rot = rand() * Math.PI;
    const color = rand() > 0.4 ? skin.stripeB : skin.stripeA;
    drawDiamond(g, cx, cy, size, rot, color, 0.4 + rand() * 0.3);
    drawDiamond(g, cx, cy, size * 0.55, rot + 0.4, skin.glow, 0.08 + rand() * 0.1);
  }

  g.rect(0, 0, w, h).fill({ color: skin.accent, alpha: 0.035 });
}

function drawDiamond(
  g: Graphics,
  cx: number,
  cy: number,
  size: number,
  rot: number,
  color: number,
  alpha: number,
): void {
  const pts: ReadonlyArray<readonly [number, number]> = [
    [0, -size],
    [size * 0.55, 0],
    [0, size],
    [-size * 0.55, 0],
  ];
  for (let i = 0; i < pts.length; i++) {
    const [lx, ly] = pts[i]!;
    const x = cx + lx * Math.cos(rot) - ly * Math.sin(rot);
    const y = cy + lx * Math.sin(rot) + ly * Math.cos(rot);
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath().fill({ color, alpha });
}


/**
 * TABLERO: el corte cruzado de verdad.
 *
 * Dos pasadas de cortacesped perpendiculares. Lo que hace que se lea como
 * cesped y no como un damero de ajedrez son los detalles: la casilla clara
 * lleva un degradado suave hacia su borde (la hierba peinada no es plana) y el
 * corte deja una linea fina de union entre casillas.
 */
function drawCheckerMow(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);

  const cols = skin.stripes;
  const cellW = w / cols;
  // Casillas cuadradas: las filas salen del ANCHO de la casilla, no de un
  // numero fijo. Con filas fijas el tablero se deforma al cambiar de modo.
  const rows = Math.max(4, Math.round(h / cellW));
  const cellH = h / rows;

  for (let cx = 0; cx < cols; cx++) {
    for (let cy = 0; cy < rows; cy++) {
      const lit = (cx + cy) % 2 === 0;
      const x = cx * cellW;
      const y = cy * cellH;
      g.rect(x, y, cellW + 1, cellH + 1).fill(lit ? skin.stripeB : skin.stripeA);
      if (lit) {
        // Peinado: la mitad de arriba de la casilla clara recibe algo mas de
        // luz, como la hierba tumbada hacia el mismo lado.
        g.rect(x, y, cellW + 1, cellH * 0.5).fill({ color: skin.accent, alpha: 0.1 });
      }
    }
  }

  // Union entre pasadas: una linea finisima donde se cruzan los cortes.
  for (let cx = 1; cx < cols; cx++) {
    g.moveTo(cx * cellW, 0).lineTo(cx * cellW, h).stroke({ width: 1, color: 0x000000, alpha: 0.05 });
  }
  for (let cy = 1; cy < rows; cy++) {
    g.moveTo(0, cy * cellH).lineTo(w, cy * cellH).stroke({ width: 1, color: 0x000000, alpha: 0.05 });
  }
}

/**
 * MAREA: ondas anchas que cruzan el campo de lado a lado.
 *
 * Las crestas no son senos perfectos sino la suma de dos ondas de distinta
 * frecuencia — una sola se lee como papel pintado, dos no repiten a la vista.
 */
function drawTide(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);

  const bands = skin.stripes * 2;
  const bandH = h / bands;
  for (let i = 0; i < bands; i++) {
    const t = i / bands;
    // Mas claro arriba: da sensacion de profundidad, como mirar al fondo.
    const mix = 0.25 + (1 - t) * 0.35;
    g.rect(0, i * bandH, w, bandH + 1).fill({ color: skin.stripeB, alpha: mix * 0.5 });
  }

  const step = 6;
  for (let i = 0; i <= bands; i++) {
    const baseY = i * bandH;
    const amp = bandH * 0.42;
    g.moveTo(0, baseY);
    for (let x = 0; x <= w; x += step) {
      const y =
        baseY +
        Math.sin((x / w) * Math.PI * 4 + i * 0.7) * amp +
        Math.sin((x / w) * Math.PI * 9 + i * 1.9) * amp * 0.35;
      g.lineTo(x, y);
    }
    g.stroke({ width: 2, color: skin.accent, alpha: 0.16 });
  }

  // Brillo de superficie: una banda clara ancha cruzando en diagonal.
  for (let i = 0; i < 3; i++) {
    g.ellipse(w * (0.3 + i * 0.22), h * 0.5, w * 0.3, h * 0.42).fill({
      color: skin.glow,
      alpha: 0.02,
    });
  }
}

/**
 * AURORA: cintas de luz sobre hierba casi negra.
 *
 * El cesped va apagado A PROPOSITO. Una aurora sobre verde brillante no se ve;
 * lo que la hace funcionar es el contraste con un suelo oscuro, igual que de
 * noche de verdad.
 */
function drawAurora(g: Graphics, skin: PitchSkin, w: number, h: number): void {
  g.rect(0, 0, w, h).fill(skin.base);

  const stripeW = w / skin.stripes;
  for (let i = 0; i < skin.stripes; i++) {
    if (i % 2 === 0) continue;
    g.rect(i * stripeW, 0, stripeW + 1, h).fill(skin.stripeB);
  }

  const rand = seeded(hashSkin(skin));
  const ribbons = 5;
  const step = 10;
  for (let r = 0; r < ribbons; r++) {
    const color = r % 2 === 0 ? skin.accent : skin.glow;
    const yBase = h * (0.12 + rand() * 0.76);
    const amp = h * (0.06 + rand() * 0.1);
    const freq = 2 + rand() * 2.5;
    const phase = rand() * Math.PI * 2;
    // Cada cinta son varias pasadas cada vez mas anchas y mas tenues: es lo
    // que da el halo, sin necesidad de filtros ni texturas.
    for (let pass = 0; pass < 4; pass++) {
      g.moveTo(0, yBase);
      for (let x = 0; x <= w; x += step) {
        g.lineTo(x, yBase + Math.sin((x / w) * Math.PI * freq + phase) * amp);
      }
      g.stroke({ width: 4 + pass * 9, color, alpha: 0.055 / (pass + 1) });
    }
  }
}

/** Pinta el cesped segun la skin elegida. */
export function drawPitchSurface(g: Graphics, skin: PitchSkin): void {
  const w = FIELD.WIDTH;
  const h = FIELD.HEIGHT;

  switch (skin.pattern) {
    case "floodlit":
      drawFloodlit(g, skin, w, h);
      break;
    case "neonHex":
      drawNeonHex(g, skin, w, h);
      break;
    case "sunburst":
      drawSunburst(g, skin, w, h);
      break;
    case "terrazzo":
      drawTerrazzo(g, skin, w, h);
      break;
    case "synthScan":
      drawSynthScan(g, skin, w, h);
      break;
    case "cropCircles":
      drawCropCircles(g, skin, w, h);
      break;
    case "herringbone":
      drawHerringbone(g, skin, w, h);
      break;
    case "splitDream":
      drawSplitDream(g, skin, w, h);
      break;
    case "magmaVein":
      drawMagmaVein(g, skin, w, h);
      break;
    case "crystalShard":
      drawCrystalShard(g, skin, w, h);
      break;
    case "checkerMow":
      drawCheckerMow(g, skin, w, h);
      break;
    case "tide":
      drawTide(g, skin, w, h);
      break;
    case "aurora":
      drawAurora(g, skin, w, h);
      break;
  }

  drawWear(g, skin, w, h);
  drawEdgeVignette(g, w, h);
}
